import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BookOpen, ClipboardList, Award, TrendingUp, Users, Inbox, GraduationCap } from "lucide-react";

const Stat = ({ icon: Icon, label, value, sub }) => (
  <div className="border border-black/10 bg-white p-5" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
    <div className="flex items-center justify-between">
      <span className="label text-clay">{label}</span>
      <Icon size={18} className="text-cinnabar" />
    </div>
    <p className="font-display font-extrabold text-3xl mt-3 text-ink">{value}</p>
    {sub && <p className="text-xs text-clay mt-1">{sub}</p>}
  </div>
);

export default function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const isStudent = user?.role === "student";
  const hi = new Date().getHours() < 12 ? "早上好" : new Date().getHours() < 18 ? "下午好" : "晚上好";

  return (
    <div>
      <div className="mb-8">
        <p className="label text-cinnabar">{hi} · Welcome back</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">{user?.name}</h1>
      </div>

      {!stats ? <p className="font-mono text-clay text-sm">加载中…</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {isStudent ? (
            <>
              <Stat icon={BookOpen} label="Enrolled" value={stats.enrolled_courses} sub="courses" />
              <Stat icon={Award} label="Completed" value={stats.completed_courses} sub="courses finished" />
              <Stat icon={ClipboardList} label="Pending" value={stats.pending_assignments} sub="assignments to do" />
              <Stat icon={TrendingUp} label="Avg Score" value={`${stats.average_score}%`} sub="across evaluations" />
            </>
          ) : (
            <>
              <Stat icon={BookOpen} label="Courses" value={stats.total_courses} sub="in catalog" />
              <Stat icon={Users} label="Students" value={stats.total_students} sub="registered" />
              <Stat icon={ClipboardList} label="Grading" value={stats.pending_grading} sub="submissions pending" />
              <Stat icon={Inbox} label="Enquiries" value={stats.new_enquiries} sub="new study-in-china leads" />
            </>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4 mt-8">
        <Link to="/dashboard/courses" className="border border-black/10 bg-white p-6 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300 group">
          <BookOpen className="text-cinnabar mb-3" size={22} />
          <h3 className="font-display font-bold text-lg group-hover:text-cinnabar transition-colors">{isStudent ? "My Courses" : "Course Catalog"}</h3>
          <p className="text-sm text-clay mt-1">{isStudent ? "Continue learning and track progress." : "Browse all programs offered."}</p>
        </Link>
        <Link to="/dashboard/assignments" className="border border-black/10 bg-white p-6 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300 group">
          <ClipboardList className="text-cinnabar mb-3" size={22} />
          <h3 className="font-display font-bold text-lg group-hover:text-cinnabar transition-colors">Assignments</h3>
          <p className="text-sm text-clay mt-1">{isStudent ? "Submit work and view feedback." : "Create tasks and grade submissions."}</p>
        </Link>
        <Link to="/dashboard/tutor" className="border border-black/10 bg-white p-6 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300 group">
          <GraduationCap className="text-cinnabar mb-3" size={22} />
          <h3 className="font-display font-bold text-lg group-hover:text-cinnabar transition-colors">AI Tutor</h3>
          <p className="text-sm text-clay mt-1">Practice Mandarin with Lǎoshī anytime.</p>
        </Link>
      </div>
    </div>
  );
}
